import React from "react";
import { Mail, Sparkles, Wand2, ListChecks, Clock, Layers, Scale } from "lucide-react";
import FeaturePage, { MockWindow, ScoreBar } from "../features/FeaturePage";
import { siblingSolutions, DEMO_CTA } from "./links";

const ACCENT = "#0025D8";

const REC = {
  Strong: "#10B981",
  Borderline: "#F59E0B",
  "Needs Work": "#E11D48",
};

// ── Hero visual: ranked shortlist ──
function ShortlistVisual() {
  const rows = [
    { name: "A. Okafor", score: 91, rec: "Strong" },
    { name: "M. Chen", score: 78, rec: "Borderline" },
    { name: "R. Patel", score: 64, rec: "Borderline" },
    { name: "J. Silva", score: 41, rec: "Needs Work" },
  ];
  return (
    <MockWindow label="RANKED SHORTLIST · ACCOUNT EXECUTIVE" badge="12 completed" accent={ACCENT}>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={r.name} className="flex items-center gap-3 rounded-xl border border-border p-3">
            <span className="font-mono text-[10px] text-muted-foreground w-4">{i + 1}</span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-[#0F172A]">{r.name}</div>
              <span className="inline-block font-mono text-[9px] px-1.5 py-0.5 rounded mt-0.5"
                style={{ background: `${REC[r.rec]}1A`, color: REC[r.rec] }}>{r.rec}</span>
            </div>
            <div className="text-right">
              <div className="font-display font-black text-lg" style={{ color: r.score >= 70 ? ACCENT : "#94A3B8" }}>{r.score}</div>
            </div>
          </div>
        ))}
      </div>
    </MockWindow>
  );
}

// ── Row visual: JD → questions ──
function GenerateVisual() {
  return (
    <MockWindow label="GENERATE FROM JOB DESCRIPTION" accent={ACCENT}>
      <div className="space-y-3">
        <div className="rounded-xl border border-border p-3 bg-secondary/40 text-xs text-muted-foreground leading-relaxed">
          "Account Executive — SaaS sales, pipeline management, discovery calls, closing…"
        </div>
        <div className="flex items-center justify-center gap-1.5 font-mono text-[10px]" style={{ color: ACCENT }}>
          <Wand2 className="w-3.5 h-3.5" /> AI generated 6 screening questions
        </div>
        <div className="space-y-2">
          {["How do you qualify a lead early in the sales cycle?", "Walk me through handling a price objection.", "How do you forecast and manage your pipeline?"].map((q, i) => (
            <div key={i} className="flex gap-2.5 rounded-xl border border-border p-2.5">
              <span className="font-mono text-[10px] mt-0.5" style={{ color: ACCENT }}>Q{i + 1}</span>
              <p className="text-xs text-[#0F172A] leading-snug">{q}</p>
            </div>
          ))}
        </div>
      </div>
    </MockWindow>
  );
}

// ── Row visual: evaluation report ──
function EvalVisual() {
  return (
    <MockWindow label="CANDIDATE EVALUATION" badge="Strong" accent={ACCENT}>
      <div className="space-y-3">
        <ScoreBar label="OVERALL SCORE" value={91} accent={ACCENT} suffix="/100 · Strong" />
        <div className="rounded-xl border border-border p-3.5">
          <div className="font-mono text-[10px] tracking-[0.15em] mb-2 flex items-center gap-1.5" style={{ color: ACCENT }}>
            <Sparkles className="w-3 h-3" /> PER-QUESTION
          </div>
          <div className="space-y-2">
            {[["Discovery", 94], ["Objection handling", 88], ["Closing", 90]].map(([l, v]) => (
              <div key={l} className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{l}</span>
                <span className="font-mono font-semibold text-[#0F172A]">{v}/100</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border p-3.5" style={{ borderColor: "#10B98133", background: "#10B9810D" }}>
          <div className="font-mono text-[10px] tracking-[0.15em] mb-1.5 text-[#047857]">SUMMARY</div>
          <p className="text-xs text-muted-foreground leading-relaxed">Strong discovery and objection-handling with concrete deal examples. Recommend advancing to a hiring-manager round.</p>
        </div>
      </div>
    </MockWindow>
  );
}

export default function AiScreeningSolution() {
  return (
    <FeaturePage
      accent={ACCENT}
      signupRole="company"
      secondaryCta={DEMO_CTA}
      eyebrow="AI SCREENING & EVALUATION"
      title="Screen every applicant."
      titleAccent="Interview only the best."
      subtitle="Send one link and let AI interview every candidate by voice, score their answers against the role, and hand you a ranked shortlist with a clear recommendation — so your team spends time only on people worth meeting."
      heroChips={["No scheduling", "Scored & ranked automatically", "Available 24/7"]}
      heroVisual={<ShortlistVisual />}
      rows={[
        {
          tag: "ONE INVITE LINK",
          title: "Interview everyone — without booking a thing.",
          body: "Invite your shortlist with a single click. Each candidate gets a private link and completes an AI-led interview whenever suits them. No calendars, no time-zone tetris, no interviewer hours burned on early-stage screens.",
          bullets: [
            "Email invites straight from the candidate list",
            "Candidates interview 24/7, no scheduling",
            "Every answer captured as a transcript",
          ],
          visual: <ShortlistVisual />,
        },
        {
          tag: "QUESTIONS FROM YOUR JD",
          title: "Great questions, generated from your job post.",
          body: "Paste a job description and AI drafts focused screening questions for the role — the tools, skills, and scenarios that actually matter. Edit, reorder, or add your own before you send.",
          bullets: [
            "AI-generated questions from any JD",
            "Tuned to the real skills for the role",
            "Fully editable before you invite",
          ],
          visual: <GenerateVisual />,
          reverse: true,
        },
        {
          tag: "SCORED EVALUATION",
          title: "A ranked shortlist, not a pile of recordings.",
          body: "Every interview is scored 0–100 with per-question feedback, a written summary, and a Strong / Borderline / Needs Work recommendation. Candidates sort themselves — you just review the top of the list.",
          bullets: [
            "Overall + per-question scores",
            "Written summary and recommendation",
            "Consistent, criteria-based evaluation",
          ],
          visual: <EvalVisual />,
        },
      ]}
      steps={[
        { title: "Set up the role", body: "Add the job, then generate or write your screening questions." },
        { title: "Invite candidates", body: "Send private interview links — candidates complete them on their own time." },
        { title: "Review the shortlist", body: "Open ranked results with scores, feedback, and a clear recommendation." },
      ]}
      useCases={[
        { icon: Clock, persona: "High-volume roles", body: "When 200 people apply, AI screens all of them overnight so you meet only the top few." },
        { icon: Layers, persona: "Lean teams", body: "No dedicated recruiter? Automate first-round screening and reclaim hours every week." },
        { icon: Scale, persona: "Consistent hiring", body: "Every candidate answers the same questions and is scored on the same criteria — fairer and more defensible." },
      ]}
      faqs={[
        { q: "Do candidates need an account?", a: "No. Each candidate gets a private link and completes the interview as a guest — no signup, no download, works in the browser." },
        { q: "How are candidates scored?", a: "Answers are evaluated 0–100 against the role, with per-question scores, a written summary, and a Strong / Borderline / Needs Work recommendation, so your shortlist is ranked automatically." },
        { q: "Can I use my own questions?", a: "Yes. Generate a starting set from your job description, then edit, reorder, or replace any question before inviting candidates." },
        { q: "Does it replace human interviews?", a: "It replaces the repetitive first-round screen. You still run the final human interviews — just with a far stronger, pre-ranked shortlist." },
        { q: "Can multiple team members review results?", a: "Yes — invite teammates as seats on your account so they can review candidates and results together." },
      ]}
      ctaTitle="Screen smarter, hire faster."
      ctaSubtitle="Start free, or book a demo to see it on your own roles."
      related={siblingSolutions("ai-screening")}
      seoTitle="AI Candidate Screening — Interview Every Applicant, Rank the Best | VetraHub"
      seoDescription="Send one link and let AI interview every candidate, score answers against the role, and return a ranked shortlist with recommendations. Screen high volumes without scheduling."
    />
  );
}
